import React from 'react';
import { Box, Hint, RawButton, Text } from '@common-fe/common-fe';
import styled from 'styled-components';

import { Roles } from '@/common/constants';
import Permissions from '@/common/permissions';
import { DropButton } from '@/components/controls/DropButton';
import { useHasAccess } from '@/modules/core/hooks';
import spacings from '@/styles/spacings';

interface StyledButtonProps {
  disabled?: boolean;
  $isDanger?: boolean;
}

const StyledActionButton = styled(RawButton)<StyledButtonProps>`
  display: block;
  width: 100%;
  text-align: left;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  padding: ${({ theme }) => `${theme.spacings.spacing4} ${theme.spacings.spacing8}`};
  border-radius: ${({ theme }) => theme.spacings.spacing4};
  ${({ theme }) => theme.fonts.text14Regular};
  color: ${({ theme, disabled }) => (disabled ? theme.colors.textDisabled : theme.colors.textBody)};

  &:hover {
    color: ${({ theme, disabled, $isDanger }) => {
      if (disabled) return theme.colors.textDisabled;
      return $isDanger ? theme.colors.danger : theme.colors.textAccent;
    }};
    background: ${({ theme, disabled, $isDanger }) => {
      if (disabled) return 'transparent';
      return $isDanger ? theme.colors.dangerContainer : theme.colors.accentContainer;
    }};
  }
`;

interface Props {
  rowKey: string;
  roleType?: Roles;
  isLoginLocked?: boolean;
  isMfaLocked?: boolean;
  canManageEmployeeAccess?: boolean;
  onProvideFullAccess: (rowKey: string) => void;
  onLock: (rowKey: string) => void;
  onUnlock: (rowKey: string) => void;
  onUnlockMfa: (rowKey: string) => void;
  onRemoveCredentials: (rowKey: string) => void;
}

const AccountActionsMenu: React.FC<Props> = ({
  rowKey,
  roleType,
  isLoginLocked,
  isMfaLocked,
  canManageEmployeeAccess = true,
  onProvideFullAccess,
  onLock,
  onUnlock,
  onUnlockMfa,
  onRemoveCredentials,
}) => {
  const hasFullAccessPermission = useHasAccess([{ permission: Permissions.EE_FULL_ACCESS }]);
  const hasLockPermission = useHasAccess([{ permission: Permissions.LOCK_EE_ACCOUNT }]);
  const hasUnlockPermission = useHasAccess([{ permission: Permissions.UNLOCK_EE_ACCOUNT }]);
  const hasMfaUnlockPermission = useHasAccess([{ permission: Permissions.EE_MFA_UNLOCK }]);
  const hasRemoveCredentialsPermission = useHasAccess([{ permission: Permissions.RESET_REGISTRATION }]);

  const showProvideFullAccess = roleType === Roles.limitedAccess && hasFullAccessPermission && canManageEmployeeAccess;
  const showLock = !isLoginLocked && hasLockPermission;
  const showUnlock = Boolean(isLoginLocked);
  const showUnlockMfa = Boolean(isMfaLocked) && hasMfaUnlockPermission && canManageEmployeeAccess;

  return (
    <DropButton
      testId={`user-accounts-security_item_${rowKey}`}
      contentStyle={{ padding: spacings.s, top: '-96px' }}
    >
      {showProvideFullAccess && (
        <StyledActionButton
          type="button"
          data-testId={`user-accounts-security_item_${rowKey}_provide-full-access`}
          onClick={() => onProvideFullAccess(rowKey)}
        >
          Provide full access
        </StyledActionButton>
      )}
      {showLock && (
        <StyledActionButton
          type="button"
          data-testId={`user-accounts-security_item_${rowKey}_lock`}
          onClick={() => onLock(rowKey)}
        >
          Lock credentials
        </StyledActionButton>
      )}
      {showUnlock && (
        hasUnlockPermission ? (
          <StyledActionButton
            type="button"
            data-testId={`user-accounts-security_item_${rowKey}_unlock`}
            onClick={() => onUnlock(rowKey)}
          >
            Unlock credentials
          </StyledActionButton>
        ) : (
          <Box direction="row" align="center" data-testId={`user-accounts-security_item_${rowKey}_unlock-disabled`}>
            <StyledActionButton type="button" disabled>Unlock credentials</StyledActionButton>
            <Hint hintWidth={200}>
              <Text size="small">You do not have permission to unlock this employee.</Text>
            </Hint>
          </Box>
        )
      )}
      {showUnlockMfa && (
        <StyledActionButton
          type="button"
          data-testId={`user-accounts-security_item_${rowKey}_unlock-mfa`}
          onClick={() => onUnlockMfa(rowKey)}
        >
          Unlock MFA
        </StyledActionButton>
      )}
      {hasRemoveCredentialsPermission ? (
        <StyledActionButton
          type="button"
          $isDanger
          data-testId={`user-accounts-security_item_${rowKey}_remove-credentials`}
          onClick={() => onRemoveCredentials(rowKey)}
        >
          Remove credentials
        </StyledActionButton>
      ) : (
        <Box data-testId={`user-accounts-security_item_${rowKey}_remove-credentials-disabled`}>
          <Hint
            hintAlign="left"
            hintWidth={200}
            hintElement={<StyledActionButton type="button" disabled>Remove credentials</StyledActionButton>}
          >
            <Text size="small" textAlign="center">You do not have permission to remove credentials</Text>
          </Hint>
        </Box>
      )}
    </DropButton>
  );
};

export default AccountActionsMenu;
